/**
 * api-client — Axios instance with auth interceptors.
 * All API calls in the app go through this client.
 * The token is automatically injected from localStorage.
 */
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || '';

export const apiClient = axios.create({
    baseURL: `${BASE_URL}/api/v1`,
    headers: { 'Content-Type': 'application/json' },
});

// ─── Request interceptor — attach access token ────────────────────────────────
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// rootClient targets the API root (no /api/v1 prefix) for endpoints mounted
// there — currently the OAuth authorization-server routes. Shares the bearer
// token from localStorage.
export const rootClient = axios.create({
    baseURL: BASE_URL,
    headers: { 'Content-Type': 'application/json' },
});
rootClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// ─── Response interceptor — auto-refresh on 401 ──────────────────────────────
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null = null) {
    failedQueue.forEach((p) => {
        if (error) p.reject(error);
        else p.resolve(token);
    });
    failedQueue = [];
}

apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const billingError = error.response?.data as { code?: string; message?: string } | undefined;
        if (billingError?.code === 'plan_limit') window.dispatchEvent(new CustomEvent('workspace-plan-limit', { detail: billingError.message }));
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // 401s from the auth endpoints themselves (login, OTP verify,
        // OTP resend, forgot/reset password) are authoritative — "wrong
        // credentials" / "bad OTP" — not "session expired." If we let the
        // refresh-and-reload path run here, the login page reloads before
        // the caller's catch block can toast an error, and the user sees
        // nothing.
        const reqURL = originalRequest?.url ?? '';
        const isAuthEndpoint = reqURL.includes('/auth/register') || reqURL.includes('/auth/login') ||
            reqURL.includes('/auth/otp') ||
            reqURL.includes('/auth/forgot-password') ||
            reqURL.includes('/auth/reset-password');

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((token) => {
                    if (originalRequest.headers) originalRequest.headers.Authorization = `Bearer ${token}`;
                    return apiClient(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
                window.location.href = '/login';
                return Promise.reject(error);
            }

            try {
                const { data } = await axios.post(`${BASE_URL}/api/v1/auth/refresh`, {
                    refresh_token: refreshToken,
                });
                localStorage.setItem('access_token', data.access_token);
                localStorage.setItem('refresh_token', data.refresh_token);
                processQueue(null, data.access_token);
                if (originalRequest.headers) originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
                return apiClient(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);

        // Capture unexpected server errors in Sentry (5xx only, skip auth noise)
        const status = error.response?.status ?? 0;
        const url = error.config?.url ?? '';
        const skipCapture =
            status === 401 || status === 403 || status === 404 ||
            url.includes('/auth/refresh') || url.includes('/health');

        if (status >= 500 && !skipCapture) {
            import('@sentry/react').then(({ captureException }) => {
                captureException(error, {
                    tags: { source: 'api', url, status: String(status) },
                });
            }).catch(() => {/* Sentry not installed */});
        }

        return Promise.reject(error);
    }
);

/** Helper: extract the API error message from an Axios error. */
export function getErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
        return error.response?.data?.message ?? error.message;
    }
    if (error instanceof Error) return error.message;
    return 'An unexpected error occurred';
}

/**
 * Helper: extract the API error code from an Axios error. Every non-2xx body
 * is {"code","message"} (backend internal/errors.APIError). Branch on this
 * rather than on the message — the message is prose and free to change, and
 * some codes (owner_must_transfer) are refusals with a fix, not failures.
 */
export function getErrorCode(error: unknown): string | undefined {
    if (axios.isAxiosError(error)) {
        const code = error.response?.data?.code;
        return typeof code === 'string' ? code : undefined;
    }
    return undefined;
}

/**
 * Resolves an avatar URL so it always works in both local dev and production.
 * The backend stores root-relative paths like /uploads/avatars/UUID.jpg.
 * In production the API lives on a different domain than the frontend, so we
 * must prepend VITE_API_URL. Absolute URLs (http/https) are passed through.
 */
export function resolveAvatarUrl(url: string | undefined | null): string | undefined {
    if (!url) return undefined;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const base = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
    return `${base}${url}`;
}

